import { Component, OnInit, ViewChild } from '@angular/core';
import { AppInformationService, ArkUnitOfWork, AuthService, UtilityService } from 'app/core/core.module';
import { Committee, CommitteeMember, Proposal, ProposalDiscussion } from 'app/core/model/entity-model';
import { DxDataGridComponent } from 'devextreme-angular';
import { alert, confirm } from 'devextreme/ui/dialog';
import _ from 'lodash';
import moment from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';
import { ProposalDialogComponent } from '../proposal-dialog/proposal-dialog.component';
import { ProposalEmailDialogComponent } from '../proposal-email-dialog/proposal-email-dialog.component';
import { ProposalVoteDialogComponent } from '../proposal-vote-dialog/proposal-vote-dialog.component';

@Component({
    selector: 'app-proposal-overview',
    templateUrl: './proposal-overview.component.html',
    styleUrls: ['./proposal-overview.component.scss'],
    standalone: false,
})
export class ProposalOverviewComponent implements OnInit {
    proposals: Proposal[] = [];
    reviewOnly = true;
    committees: any;
    executiveMembers: any;
    processing = false;
    showAll = false;
    executiveAdmin = false;

    @ViewChild('dataGrid') dataGrid: DxDataGridComponent;

    constructor(
        private uow: ArkUnitOfWork,
        private auth: AuthService,
        private modalService: BsModalService,
        public appInfo: AppInformationService,
        private utility: UtilityService
    ) {}

    async ngOnInit() {
        this.committees = this.uow.lookups.allCommittees;
        this.executiveMembers = (await this.uow.getMembersByCommitteeIds([Committee.Committees.ExecutiveCommittee])) as CommitteeMember[];
        this.executiveAdmin = this.auth.hasRole('Executive:Admin');
        if (this.executiveAdmin) this.showAll = !this.showAll;
        await this.getData();
        this.reviewOnly = !this.auth.hasRole('Executive:Clerk');
    }

    async getData() {
        this.appInfo.pleaseWaitShow();
        const executiveMember = _.find(this.executiveMembers, { personId: this.auth.currentUser.personId });
        const startDate = !this.auth.hasRole('Executive:Clerk') ? (executiveMember ? moment(executiveMember.startDate).add(-1, 'months').toDate() : new Date()) : null;
        const proposals = (await this.uow.getProposals(startDate, 'proposalVotes', this.showAll ? null : 25)) as Proposal[];
        this.proposals = proposals;

        this.appInfo.pleaseWaitHide();
    }

    async toggleShowAll() {
        this.showAll = !this.showAll;
        await this.getData();
    }

    onSelectionChanged(e) {
        const rows = this.dataGrid.instance.getSelectedRowsData();
    }

    onToolbarPreparing(e) {
        e.toolbarOptions.items.unshift(
            {
                template: 'toolbarText',
                location: 'before',
            },
            {
                location: 'before',
                template: 'toolbarButtons',
            }
        );
    }

    rowDoubleClick(e) {
        if (!!this.reviewOnly) return;
        this.add(e.data);
    }

    rowPrepare(e) {
        if (e.rowType === 'data') {
            if (!!e.data.approved) {
                e.rowElement.className = e.rowElement.className + ' approved';
            }
            if (!!e.data.declined) {
                e.rowElement.className = e.rowElement.className + ' declined';
            }
            if (!e.data.approved && !e.data.declined) {
                e.rowElement.className = e.rowElement.className + ' waiting-for-vote';
            }
        }
    }

    async add(selectedRow?: any) {
        const modalRef = this.modalService.show(ProposalDialogComponent, {
            class: 'modal-md',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                proposal: !!selectedRow ? selectedRow : null,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                if (result.initialDiscussion) {
                    const discussion = this.uow.proposalDiscussions.createEntity() as ProposalDiscussion;
                    discussion.proposalId = result.value.id;
                    discussion.note = result.initialDiscussion;
                    discussion.created = new Date();
                    discussion.personId = this.auth.currentUser.personId != null ? this.auth.currentUser.personId : 3669;
                    await this.uow.commit();
                }
                await this.getData();
            }
            modalRef.hide();
        });
    }

    vote(e?: any) {
        let selectedRow = null;
        if (!e) selectedRow = this.dataGrid.instance.getSelectedRowsData()[0];
        else selectedRow = e;
        if (!selectedRow) return;
        const modalRef = this.modalService.show(ProposalVoteDialogComponent, {
            class: 'modal-lg',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                proposal: !!selectedRow ? selectedRow : null,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (result.success) {
                await this.getData();
            }
            modalRef.hide();
        });
    }

    async approveDecline(mode: any) {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        if (!selectedRows) return;

        this.appInfo.pleaseWaitShow();
        selectedRows.forEach((row) => {
            if (mode === 'approve') {
                row.approved = new Date();
                row.declined = null;
            }
            if (mode === 'decline') {
                row.approved = null;
                row.declined = new Date();
            }
            if (mode === 'clear') {
                row.approved = null;
                row.declined = null;
            }
        });
        await this.uow.commit();
        this.appInfo.pleaseWaitHide();
    }

    async emailPropsal(mode?: string) {
        const selectedRow = this.dataGrid.instance.getSelectedRowsData()[0];
        if (!selectedRow) return;

        if (!!selectedRow.emailed && mode !== 'decision') {
            const response = await confirm(
                `This proposal was already sent on ${moment(selectedRow.emailed).format('MM/DD/YYYY')}.  Send again?`,
                `${this.appInfo.settings.church.toUpperCase()}`
            );
            if (!response) return;
        }
        this.processing = true;

        const modalRef = this.modalService.show(ProposalEmailDialogComponent, {
            class: 'modal-md',
            ignoreBackdropClick: true,
            keyboard: false,
            initialState: {
                proposal: !!selectedRow ? selectedRow : null,
            },
        });

        modalRef.content.result.subscribe(async (result) => {
            if (!!result && result.value) {
                const peopleIds = result.value.map((x) => {
                    return x.personId;
                });
                this.appInfo.pleaseWaitShow();
                let response = '';
                if (mode !== 'decision' && !result.congregationalVote) {
                    response = await this.utility.emailProposal(peopleIds, selectedRow.id, this.auth.currentUser.userName, result.sendAsText);
                } else if (result.congregationalVote) {
                    response = await this.utility.generateCongregationalProposalVotes(peopleIds, selectedRow.id, this.auth.currentUser.userName);
                } else {
                    response = await this.utility.emailProposalReport(peopleIds, selectedRow.id, this.auth.currentUser.userName);
                }
                this.appInfo.pleaseWaitHide();
                const status = alert(
                    `${result.congregationalVote ? 'Votes created' : result.sendAsText ? 'Text Sent!' : 'Email Sent!'}`,
                    `${this.appInfo.settings.church.toUpperCase()}`
                );
            }
            modalRef.hide();
            this.processing = false;
            await this.getData();
        });
    }

    async print() {
        const selectedRow = this.dataGrid.instance.getSelectedRowsData()[0];
        if (!selectedRow) return;
        await this.utility.printProposal(selectedRow.id);
    }

    async clear() {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        if (!selectedRows) return;

        this.appInfo.pleaseWaitShow();
        selectedRows.forEach((row) => {
            row.approved = null;
            row.declined = null;
        });
        await this.uow.commit();
        // await this.getData();
        this.appInfo.pleaseWaitHide();
    }

    async delete() {
        const selectedRows = this.dataGrid.instance.getSelectedRowsData();
        if (!selectedRows) return;

        this.appInfo.pleaseWaitShow();
        selectedRows.forEach((row) => {
            row.deleted = new Date();
        });
        await this.uow.commit();
        await this.getData();
        this.appInfo.pleaseWaitHide();
    }
}
